import {
  USERS_ACCESS_ANALYTICS,
  USERS_ACCESS_ROLE_LABEL_KEYS,
  USERS_ACCESS_STATE_LABEL_KEYS,
  USERS_ACCESS_TYPE_LABEL_KEYS,
} from '../../constants/usersAccess'
import type {
  UsersAccessFilterCountsI,
  UsersAccessFilterValuesI,
} from '../../types/usersAccess'
import type { UsersAccessFilterFormInputsI } from '../../types/usersAccessFilters'
import { useTranslation } from 'react-i18next'

import type { ColorScheme } from '@hivewatch/dark'

import { FilterDrawer } from '@/components/filter-drawer'
import { trackEvent } from '@/utils/analytics'

import { UsersAccessFilterForm } from './UsersAccessFilterForm'

const FILTER_FIELD_NAMES: Array<keyof UsersAccessFilterFormInputsI> = [
  'roles',
  'accountStates',
  'accountTypes',
]

interface UsersAccessFilterDrawerIProps {
  colorScheme: ColorScheme
  counts: UsersAccessFilterCountsI
  filters: UsersAccessFilterValuesI
  isOpen: boolean
  onApply: (filters: UsersAccessFilterValuesI) => void
  onClose: () => void
}

export const UsersAccessFilterDrawer = ({
  colorScheme,
  counts,
  filters,
  isOpen,
  onApply,
  onClose,
}: UsersAccessFilterDrawerIProps) => {
  const { t } = useTranslation()
  const defaultValues: UsersAccessFilterFormInputsI = {
    roles: filters.roles.map((value) => ({
      value,
      label: t(USERS_ACCESS_ROLE_LABEL_KEYS[value]),
    })),
    accountStates: filters.accountStates.map((value) => ({
      value,
      label: t(USERS_ACCESS_STATE_LABEL_KEYS[value]),
    })),
    accountTypes: filters.accountTypes.map((value) => ({
      value,
      label: t(USERS_ACCESS_TYPE_LABEL_KEYS[value]),
    })),
  }

  const applyFilters = (values: UsersAccessFilterFormInputsI) => {
    const nextFilters: UsersAccessFilterValuesI = {
      roles: values.roles?.map(({ value }) => value) ?? [],
      accountStates: values.accountStates?.map(({ value }) => value) ?? [],
      accountTypes: values.accountTypes?.map(({ value }) => value) ?? [],
    }
    onApply(nextFilters)
    trackEvent(USERS_ACCESS_ANALYTICS.filtersApplied, {
      roles: nextFilters.roles,
      account_states: nextFilters.accountStates,
      account_types: nextFilters.accountTypes,
    })
  }

  return (
    <FilterDrawer<UsersAccessFilterFormInputsI>
      colorScheme={colorScheme}
      defaultValues={defaultValues}
      fieldNames={FILTER_FIELD_NAMES}
      isOpen={isOpen}
      onApply={applyFilters}
      onClear={() => trackEvent(USERS_ACCESS_ANALYTICS.filtersCleared)}
      onClose={onClose}
      title={t('settings:rebrand_users_filter_title')}
    >
      {(methods) => (
        <UsersAccessFilterForm
          colorScheme={colorScheme}
          control={methods.control}
          counts={counts}
        />
      )}
    </FilterDrawer>
  )
}
